import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../components/layout';
import { Alert } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Dialog } from '../components/ui/dialog';
import { Field } from '../components/ui/field';
import { Checkbox, Input, Select } from '../components/ui/input';
import { api, errorMessage } from '../lib/api';
import { can, useMe } from '../lib/session';
import type { AdapterInfo, DeliveryRow, IntegrationRow, JsonSchemaObject } from '../lib/types';
import { formatDateTime } from '../lib/utils';

const adaptersQuery = {
  queryKey: ['integrations', 'adapters'],
  queryFn: () => api<{ items: AdapterInfo[] }>('/integrations/adapters'),
};

function statusVariant(status: IntegrationRow['status']) {
  return status === 'active' ? 'success' : status === 'degraded' ? 'destructive' : 'secondary';
}

/** Внешние системы: статус, ошибки, очередь доставки с повтором. */
export function IntegrationsPage() {
  const { t, i18n } = useTranslation();
  const me = useMe();
  const queryClient = useQueryClient();
  const list = useQuery({
    queryKey: ['integrations'],
    queryFn: () => api<{ items: IntegrationRow[] }>('/integrations'),
  });
  const [editing, setEditing] = useState<IntegrationRow | 'new'>();
  const [deliveriesOf, setDeliveriesOf] = useState<IntegrationRow>();
  const [error, setError] = useState<string>();
  const manage = can(me, 'integrations.manage');

  const act = async (path: string) => {
    setError(undefined);
    try {
      await api(path, { method: 'POST' });
      await queryClient.invalidateQueries({ queryKey: ['integrations'] });
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <>
      <PageHeader
        title={t('intg.title')}
        description={t('intg.hint')}
        actions={
          manage ? (
            <Button
              onClick={() => {
                setEditing('new');
              }}
            >
              {t('intg.add')}
            </Button>
          ) : null
        }
      />
      {error ? (
        <Alert variant="destructive" className="mb-4">
          {error}
        </Alert>
      ) : null}
      {list.data?.items.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('intg.empty')}</p>
      ) : null}
      <div className="grid gap-3 md:grid-cols-2">
        {(list.data?.items ?? []).map((i) => (
          <Card key={i.id}>
            <CardHeader
              title={i.name}
              description={`${i.adapter} · ${i.kind}`}
              actions={
                <Badge variant={statusVariant(i.status)}>{t(`intg.statuses.${i.status}`)}</Badge>
              }
            />
            <CardContent className="grid gap-2 text-sm">
              <p className="text-muted-foreground">
                {t('intg.lastSuccess')}:{' '}
                {i.lastSuccessAt ? formatDateTime(i.lastSuccessAt, i18n.language) : '—'}
              </p>
              {i.lastError ? (
                <p className="text-destructive">
                  {i.lastErrorAt ? `${formatDateTime(i.lastErrorAt, i18n.language)}: ` : ''}
                  {i.lastError}
                </p>
              ) : null}
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setDeliveriesOf(i);
                  }}
                >
                  {t('intg.deliveries')}
                  {i.failedDeliveries ? ` (${String(i.failedDeliveries)} ✗)` : ''}
                </Button>
                {manage ? (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setEditing(i);
                      }}
                    >
                      {t('common.edit')}
                    </Button>
                    {i.failedDeliveries ? (
                      <Button size="sm" onClick={() => void act(`/integrations/${i.id}/retry`)}>
                        {t('intg.retryAll')}
                      </Button>
                    ) : null}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        void act(
                          `/integrations/${i.id}/${i.status === 'disabled' ? 'enable' : 'disable'}`,
                        )
                      }
                    >
                      {i.status === 'disabled' ? t('intg.enable') : t('intg.disable')}
                    </Button>
                  </>
                ) : null}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      {editing ? (
        <IntegrationDialog
          integration={editing === 'new' ? undefined : editing}
          onClose={() => {
            setEditing(undefined);
          }}
        />
      ) : null}
      {deliveriesOf ? (
        <DeliveriesDialog
          integration={deliveriesOf}
          onClose={() => {
            setDeliveriesOf(undefined);
          }}
        />
      ) : null}
    </>
  );
}

const EVENTS = [
  'orders.status_changed',
  'billing.document_issued',
  'payments.refund_succeeded',
  'returns.status_changed',
] as const;

function IntegrationDialog({
  integration,
  onClose,
}: {
  integration?: IntegrationRow;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const adapters = useQuery(adaptersQuery);
  const [adapterCode, setAdapterCode] = useState(integration?.adapter ?? '');
  const [name, setName] = useState(integration?.name ?? '');
  const [config, setConfig] = useState<Record<string, unknown>>(integration?.config ?? {});
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [error, setError] = useState<string>();
  const adapter =
    adapters.data?.items.find((a) => a.code === adapterCode) ?? adapters.data?.items[0];
  const events = (config.events as string[] | undefined) ?? adapter?.defaultEvents ?? [];

  const fields = (schema: JsonSchemaObject | undefined) =>
    Object.entries(schema?.properties ?? {}).filter(([key]) => key !== 'events');

  const save = async () => {
    if (!adapter) return;
    setError(undefined);
    try {
      await api(integration ? `/integrations/${integration.id}` : '/integrations', {
        method: integration ? 'PUT' : 'POST',
        body: { adapter: adapter.code, name, config: { ...config, events }, credentials },
      });
      await queryClient.invalidateQueries({ queryKey: ['integrations'] });
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={integration ? integration.name : t('intg.add')}
      className="max-w-xl"
      footer={
        <Button disabled={name.trim().length < 2 || !adapter} onClick={() => void save()}>
          {t('common.save')}
        </Button>
      }
    >
      <div className="grid gap-3 text-sm">
        {!integration ? (
          <Field label={t('intg.adapter')}>
            {(id) => (
              <Select
                id={id}
                value={adapter?.code ?? ''}
                onChange={(e) => {
                  setAdapterCode(e.target.value);
                  setConfig({});
                }}
              >
                {(adapters.data?.items ?? []).map((a) => (
                  <option key={a.code} value={a.code}>
                    {a.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        ) : null}
        <Field label={t('intg.name')}>
          {(id) => (
            <Input
              id={id}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
            />
          )}
        </Field>
        {fields(adapter?.config).map(([key, prop]) =>
          prop.type === 'boolean' ? (
            <label key={key} className="flex items-center gap-2">
              <Checkbox
                checked={config[key] === true}
                onChange={(e) => {
                  setConfig((c) => ({ ...c, [key]: e.target.checked }));
                }}
              />
              {key}
            </label>
          ) : (
            <Field key={key} label={key}>
              {(id) => (
                <Input
                  id={id}
                  value={typeof config[key] === 'string' ? config[key] : ''}
                  onChange={(e) => {
                    setConfig((c) => ({ ...c, [key]: e.target.value }));
                  }}
                />
              )}
            </Field>
          ),
        )}
        {fields(adapter?.credentials).map(([key]) => (
          <Field
            key={key}
            label={`${key} 🔒`}
            hint={integration?.credentials[key] ? t('intg.keepSecret') : undefined}
          >
            {(id) => (
              <Input
                id={id}
                type="password"
                autoComplete="off"
                placeholder={integration?.credentials[key] ?? ''}
                value={credentials[key] ?? ''}
                onChange={(e) => {
                  setCredentials((c) => ({ ...c, [key]: e.target.value }));
                }}
              />
            )}
          </Field>
        ))}
        <fieldset className="grid gap-1">
          <legend className="font-medium">{t('intg.events')}</legend>
          {EVENTS.map((ev) => (
            <label key={ev} className="flex items-center gap-2">
              <Checkbox
                checked={events.includes(ev)}
                onChange={(e) => {
                  setConfig((c) => ({
                    ...c,
                    events: e.target.checked ? [...events, ev] : events.filter((x) => x !== ev),
                  }));
                }}
              />
              {t(`intg.eventNames.${ev.replace('.', '_') as 'orders_status_changed'}`)}
            </label>
          ))}
        </fieldset>
        {error ? <Alert variant="destructive">{error}</Alert> : null}
      </div>
    </Dialog>
  );
}

function DeliveriesDialog({
  integration,
  onClose,
}: {
  integration: IntegrationRow;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const me = useMe();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<'' | DeliveryRow['status']>('');
  const deliveries = useQuery({
    queryKey: ['integrations', integration.id, 'deliveries', status],
    queryFn: () =>
      api<{ items: DeliveryRow[] }>(
        `/integrations/${integration.id}/deliveries${status ? `?status=${status}` : ''}`,
      ),
  });
  const retry = async (id: string) => {
    await api(`/integrations/deliveries/${id}/retry`, { method: 'POST' });
    await queryClient.invalidateQueries({ queryKey: ['integrations'] });
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={`${t('intg.deliveries')} · ${integration.name}`}
      className="max-w-3xl"
    >
      <div className="grid gap-3 text-sm">
        <Select
          aria-label={t('po.status')}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as '' | DeliveryRow['status']);
          }}
          className="w-48"
        >
          <option value="">{t('po.allStatuses')}</option>
          {(['pending', 'succeeded', 'failed', 'dead'] as const).map((s) => (
            <option key={s} value={s}>
              {t(`intg.deliveryStatuses.${s}`)}
            </option>
          ))}
        </Select>
        <table className="w-full">
          <tbody>
            {(deliveries.data?.items ?? []).map((d) => (
              <tr key={d.id} className="border-b last:border-0">
                <td className="py-1.5 text-xs text-muted-foreground">
                  {formatDateTime(d.createdAt, i18n.language)}
                </td>
                <td>{d.eventType}</td>
                <td>
                  <Badge
                    variant={
                      d.status === 'dead' || d.status === 'failed' ? 'destructive' : 'secondary'
                    }
                  >
                    {t(`intg.deliveryStatuses.${d.status}`)}
                  </Badge>
                </td>
                <td className="text-xs">
                  {d.attempts} × {d.lastError ?? ''}
                </td>
                <td className="text-right">
                  {(d.status === 'dead' || d.status === 'failed') &&
                  can(me, 'integrations.manage') ? (
                    <Button size="sm" variant="outline" onClick={() => void retry(d.id)}>
                      {t('intg.retry')}
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Dialog>
  );
}
